import { ReloadOutlined, WarningOutlined } from '@ant-design/icons';
import {
  Alert,
  Badge,
  Card,
  Input,
  Modal,
  Select,
  Space,
  Spin,
  Tooltip,
  Typography,
  message,
} from 'antd';
import { useCallback, useEffect, useState } from 'react';
import type { DragEvent, ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';

import { ApiError } from '../api/errors';
import { ProjectStatus, changeProjectStatus, listProjects } from '../api/projects';
import type { ProjectListItem, ProjectStatusValue } from '../api/projects';
import { PermissionCodes } from '../auth/permissions';
import { useAuth } from '../auth/useAuth';
import { AppRoutes } from '../routes';
import {
  formatDate,
  healthTag,
  priorityOptions,
  priorityTag,
  progressBar,
  projectStatusLabel,
} from './projects/projectDisplay';
import type { ProjectPriorityValue } from '../api/projects';
import { BrandColors } from '../theme/brand';

/**
 * Các cột của bảng, theo đúng thứ tự vòng đời.
 *
 * <b>KHÔNG có cột "Đã đóng" và "Đã hủy", và đây là quyết định có chủ ý.</b> Hai trạng thái đó là
 * trạng thái cuối — không quay lui được — nên biến chúng thành một thao tác kéo thả là mời gọi tai
 * nạn: một cú kéo trượt tay sẽ đóng nhầm một dự án đang chạy, và hậu quả là phải mở dự án mới trong
 * khi mọi chứng từ đã lập vẫn trỏ về mã cũ.
 *
 * Đóng và hủy dự án vẫn làm được ở trang chi tiết, nơi có cảnh báo rõ ràng đi kèm.
 */
const boardColumns: readonly ProjectStatusValue[] = [
  ProjectStatus.Draft,
  ProjectStatus.InProgress,
  ProjectStatus.OnHold,
  ProjectStatus.Installing,
  ProjectStatus.PendingAcceptance,
  ProjectStatus.UnderWarranty,
];

/**
 * Số dự án nạp cho bảng.
 *
 * Bằng giới hạn lớn nhất của máy chủ. Bảng này không phân trang được — một bảng Kanban chia trang là
 * một bảng nói dối, vì người dùng tưởng cột đã đầy đủ. Khi số dự án chưa đóng vượt ngưỡng này thì
 * bảng hiện cảnh báo thay vì âm thầm thiếu, xem phần dựng bên dưới.
 */
const boardPageSize = 200;

/**
 * Bảng tiến độ dự án theo trạng thái.
 *
 * <b>Kéo thẻ sang cột khác để đổi trạng thái.</b> Dùng cơ chế kéo thả sẵn có của trình duyệt thay vì
 * thêm một thư viện: thao tác này chỉ cần kéo một thẻ giữa sáu cột, không cần sắp xếp lại thứ tự
 * trong cột hay kéo lồng nhau — và mỗi thư viện thêm vào là một thứ phải theo dõi lỗ hổng bảo mật.
 *
 * <b>Bảng và danh sách dùng chung một điểm cuối.</b> Bảng Kanban chỉ là cách hiển thị khác của cùng
 * dữ liệu, gom theo trạng thái. Một điểm cuối riêng cho bảng sẽ có nghĩa là hai chỗ cùng cài đặt quy
 * tắc phạm vi dữ liệu và quy tắc quá hạn.
 */
export function ProjectBoardPage(): ReactNode {
  const { hasPermission } = useAuth();
  const navigate = useNavigate();

  const canChangeStatus = hasPermission(PermissionCodes.ProjectChangeStatus);

  const [projects, setProjects] = useState<readonly ProjectListItem[] | null>(null);
  const [totalCount, setTotalCount] = useState(0);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [reloadToken, setReloadToken] = useState(0);

  const [searchTerm, setSearchTerm] = useState('');
  const [priorityFilter, setPriorityFilter] = useState<ProjectPriorityValue | null>(null);

  const [draggingId, setDraggingId] = useState<string | null>(null);
  const [hoverColumn, setHoverColumn] = useState<ProjectStatusValue | null>(null);

  const [holdTarget, setHoldTarget] = useState<ProjectListItem | null>(null);
  const [holdReason, setHoldReason] = useState('');
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    let isCurrent = true;

    async function load(): Promise<void> {
      setErrorMessage(null);

      try {
        const page = await listProjects({
          searchTerm,
          onlyOpen: true,
          pageSize: boardPageSize,
          ...(priorityFilter === null ? {} : { priority: priorityFilter }),
        });

        if (!isCurrent) {
          return;
        }

        setProjects(page.items);
        setTotalCount(page.totalCount);
      } catch (error) {
        if (!isCurrent) {
          return;
        }

        setErrorMessage(
          error instanceof ApiError ? error.message : 'Không tải được bảng tiến độ.',
        );
      }
    }

    void load();

    return () => {
      isCurrent = false;
    };
  }, [searchTerm, priorityFilter, reloadToken]);

  const reload = useCallback(() => {
    setReloadToken((token) => token + 1);
  }, []);

  async function applyStatus(
    project: ProjectListItem,
    status: ProjectStatusValue,
    reason: string | null,
  ): Promise<void> {
    setIsSaving(true);

    try {
      await changeProjectStatus(project.id, { status, holdReason: reason });

      message.success(`${project.code}: ${projectStatusLabel(status)}.`);
      setHoldTarget(null);
      setHoldReason('');
      reload();
    } catch (error) {
      message.error(
        error instanceof ApiError ? error.message : 'Không đổi được trạng thái dự án.',
      );
    } finally {
      setIsSaving(false);
    }
  }

  function handleDrop(status: ProjectStatusValue): void {
    setHoverColumn(null);

    const project = projects?.find((candidate) => candidate.id === draggingId);

    setDraggingId(null);

    if (project === undefined || project.status === status) {
      return;
    }

    /*
      Tạm dừng bắt buộc kèm lý do, nên nó mở hộp thoại thay vì lưu ngay. Gửi thẳng rồi để máy chủ từ
      chối cũng đúng, nhưng người dùng sẽ thấy thẻ nhảy về chỗ cũ kèm một thông báo lỗi — trong khi
      thứ họ cần là một ô để gõ lý do.
    */
    if (status === ProjectStatus.OnHold) {
      setHoldTarget(project);
      setHoldReason('');

      return;
    }

    void applyStatus(project, status, null);
  }

  if (errorMessage !== null) {
    return <Alert type="error" showIcon message={errorMessage} />;
  }

  if (projects === null) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', padding: 48 }}>
        <Spin size="large" />
      </div>
    );
  }

  return (
    <Space direction="vertical" size="middle" style={{ width: '100%' }}>
      <Space style={{ justifyContent: 'space-between', width: '100%' }} wrap>
        <Typography.Title level={4} style={{ margin: 0 }}>
          Bảng tiến độ
        </Typography.Title>
        <Space wrap>
          <Input.Search
            allowClear
            placeholder="Tìm theo mã hoặc tên"
            style={{ width: 240 }}
            onSearch={setSearchTerm}
          />
          <Select<ProjectPriorityValue>
            allowClear
            placeholder="Ưu tiên"
            style={{ width: 140 }}
            options={priorityOptions}
            onChange={(value: ProjectPriorityValue | undefined) => {
              setPriorityFilter(value ?? null);
            }}
          />
          <Tooltip title="Tải lại">
            <ReloadOutlined onClick={reload} style={{ fontSize: 16, cursor: 'pointer' }} />
          </Tooltip>
        </Space>
      </Space>

      {totalCount > projects.length ? (
        <Alert
          type="warning"
          showIcon
          message={`Bảng đang hiện ${projects.length.toString()} trong ${totalCount.toString()} dự án chưa đóng.`}
          description="Bảng không chia trang được — một bảng Kanban chia trang sẽ làm người dùng tưởng cột đã đầy đủ. Hãy thu hẹp bằng ô tìm kiếm hoặc bộ lọc ưu tiên."
        />
      ) : null}

      {!canChangeStatus ? (
        <Alert
          type="info"
          showIcon
          message="Bạn không có quyền đổi trạng thái dự án, nên bảng này chỉ để xem."
        />
      ) : null}

      <div style={{ display: 'flex', gap: 12, overflowX: 'auto', alignItems: 'flex-start' }}>
        {boardColumns.map((status) => {
          const columnProjects = projects.filter((project) => project.status === status);

          return (
            <div
              key={status}
              onDragOver={(event) => {
                if (!canChangeStatus) {
                  return;
                }

                // Bắt buộc chặn hành vi mặc định, nếu không trình duyệt sẽ không cho thả xuống đây.
                event.preventDefault();
                setHoverColumn(status);
              }}
              onDragLeave={() => {
                setHoverColumn(null);
              }}
              onDrop={() => {
                handleDrop(status);
              }}
              style={{
                minWidth: 260,
                flex: '1 0 260px',
                background: hoverColumn === status ? BrandColors.blueSoft : BrandColors.surfaceSubtle,
                border: `1px solid ${hoverColumn === status ? BrandColors.blue : BrandColors.border}`,
                borderRadius: 12,
                padding: 10,
              }}
            >
              <Space style={{ justifyContent: 'space-between', width: '100%', marginBottom: 8 }}>
                <Typography.Text strong>{projectStatusLabel(status)}</Typography.Text>
                <Badge
                  count={columnProjects.length}
                  showZero
                  style={{ backgroundColor: BrandColors.border, color: BrandColors.ink, boxShadow: 'none' }}
                />
              </Space>

              <Space direction="vertical" size={8} style={{ width: '100%' }}>
                {columnProjects.map((project) => (
                  <Card
                    key={project.id}
                    size="small"
                    hoverable
                    draggable={canChangeStatus}
                    onDragStart={(event: DragEvent<HTMLDivElement>) => {
                      setDraggingId(project.id);

                      // Một số trình duyệt chỉ bắt đầu kéo khi có dữ liệu được gán.
                      event.dataTransfer.setData('text/plain', project.id);
                      event.dataTransfer.effectAllowed = 'move';
                    }}
                    onDragEnd={() => {
                      setDraggingId(null);
                      setHoverColumn(null);
                    }}
                    onClick={() => {
                      void navigate(`${AppRoutes.Project}/${project.id}`);
                    }}
                    style={{ opacity: draggingId === project.id ? 0.5 : 1 }}
                  >
                    <Space direction="vertical" size={4} style={{ width: '100%' }}>
                      <Space size={4} wrap>
                        <Typography.Text strong style={{ fontSize: 12 }}>
                          {project.code}
                        </Typography.Text>
                        {priorityTag(project.priority)}
                        {healthTag(project.health, null)}
                      </Space>

                      <Typography.Text style={{ fontSize: 13 }}>{project.name}</Typography.Text>

                      {progressBar(project.plannedProgressPercent, project.actualProgressPercent)}

                      <Space size={4}>
                        <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                          Hạn {formatDate(project.committedCompletionDate)}
                        </Typography.Text>
                        {project.isOverdue ? (
                          <Tooltip title="Đã quá mốc cam kết mà chưa kết thúc thực tế.">
                            <WarningOutlined style={{ color: BrandColors.red }} />
                          </Tooltip>
                        ) : null}
                      </Space>
                    </Space>
                  </Card>
                ))}
              </Space>
            </div>
          );
        })}
      </div>

      <Typography.Text type="secondary" style={{ fontSize: 12 }}>
        Đóng và hủy dự án làm ở trang chi tiết. Hai trạng thái đó không quay lui được, nên chúng cố ý
        không phải là một thao tác kéo thả.
      </Typography.Text>

      <Modal
        open={holdTarget !== null}
        title={holdTarget === null ? 'Tạm dừng dự án' : `Tạm dừng ${holdTarget.code}`}
        okText="Tạm dừng"
        cancelText="Đóng"
        confirmLoading={isSaving}
        okButtonProps={{ disabled: holdReason.trim() === '' }}
        onCancel={() => {
          setHoldTarget(null);
        }}
        onOk={() => {
          if (holdTarget !== null) {
            void applyStatus(holdTarget, ProjectStatus.OnHold, holdReason);
          }
        }}
      >
        <Space direction="vertical" size="small" style={{ width: '100%' }}>
          <Alert
            type="info"
            showIcon
            message="Bắt buộc ghi lý do"
            description="Người quay lại dự án sau vài tháng cần biết vì sao nó dừng, và nguyên nhân thường nằm ở phía khách."
          />
          <Input.TextArea
            rows={3}
            value={holdReason}
            placeholder="Khách chưa dừng được dây chuyền để lắp đặt."
            onChange={(event) => {
              setHoldReason(event.target.value);
            }}
          />
        </Space>
      </Modal>
    </Space>
  );
}
